import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ResourceGrid from '../../../components/resource-grid/ResourceGrid';
import { getPracticeClosures } from '../../../services/clousures/practiceClosureService';
import { getPracticesList } from '../../../services/practices/getPractices';
import { PracticeClosure } from '../../../types/closures/types';

interface MarketClinicClosuresProps {
  marketId: string;
}

type PracticeClosureWithName = PracticeClosure & {
  practiceName: string;
};

const MarketClinicClosures: React.FC<MarketClinicClosuresProps> = ({
  marketId,
}) => {
  const [closures, setClosures] = useState<
    PracticeClosureWithName[] | null
  >(null);
  const [loadingClosures, setLoadingClosures] = useState(false);
  const [errorClosures, setErrorClosures] = useState<string | null>(
    null
  );
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMarketData = async () => {
      setLoadingClosures(true);
      try {
        // Fetch closures first
        const closuresData = await getPracticeClosures({ marketId });

        // Fetch all practices for the market (not just 10)
        const practicesMap: Record<string, string> = {};
        let startToken: string | null = null;
        let hasMore = true;

        while (hasMore) {
          const practicesResponse = await getPracticesList(
            startToken,
            { marketIds: marketId },
            100
          );
          if (practicesResponse.data?.practices) {
            practicesResponse.data.practices.forEach((practice) => {
              practicesMap[practice.id] = practice.name;
            });
            // Check if there are more pages
            startToken = practicesResponse.data.nextToken || null;
            hasMore = !!startToken;
          } else {
            hasMore = false;
          }
        }

        const closuresWithNames = closuresData.map((closure) => ({
          ...closure,
          practiceName:
            practicesMap[closure.practiceId] || 'Unknown Practice',
        }));

        setClosures(closuresWithNames);
      } catch (error) {
        console.error('Error fetching market data:', error);
        setErrorClosures('Failed to load clinic closures.');
      } finally {
        setLoadingClosures(false);
      }
    };

    fetchMarketData();
  }, [marketId]);

  const closureColumns = [
    {
      header: 'Practice',
      accessor: 'practiceName',
      width: '220px',
      render: (row: PracticeClosureWithName) => (
        <Text
          fontSize="sm"
          color="blue.500"
          _hover={{
            cursor: 'pointer',
            textDecoration: 'underline',
            color: 'blue.700',
          }}
          onClick={() => {
            if (row.practiceId) {
              navigate(`/practices/${row.practiceId}`);
            } else {
              console.error('Practice ID is missing for navigation.');
            }
          }}
        >
          {row.practiceName}
        </Text>
      ),
    },
    {
      header: 'Start Date',
      accessor: 'startDate',
      width: '180px',
    },
    {
      header: 'End Date',
      accessor: 'endDate',
      width: '180px',
    },
  ];

  return (
    <Box
      padding="1.5rem"
      borderRadius="0.5rem"
      border="1px solid"
      borderColor="gray.200"
      width="100%"
      mb="3rem"
      mt="1.75rem"
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        mb="1rem"
      >
        <Text fontSize="lg" fontWeight="bold" color="gray.700">
          Market Clinic Closures
        </Text>
      </Flex>
      {loadingClosures ? (
        <Flex align="center" justify="center" minH="120px">
          <Spinner size="lg" />
        </Flex>
      ) : errorClosures ? (
        <Text color="red.500" textAlign="center">
          {errorClosures}
        </Text>
      ) : closures?.length ? (
        <ResourceGrid<PracticeClosureWithName>
          columns={closureColumns}
          data={closures}
        />
      ) : (
        <Flex
          direction="column"
          align="center"
          justify="center"
          minH="300px"
          textAlign="center"
        >
          <Text
            fontSize="1.25rem"
            fontWeight="600"
            color="text.secondary"
            mb="0.5rem"
          >
            No Clinic Closures Found
          </Text>
          <Text
            fontSize="0.875rem"
            color="gray.600"
            mb="1.5rem"
            maxW="400px"
          >
            Use "Manage Practices" to add practices - clinic closures will show here once practices are added.
          </Text>
          <Button
            colorScheme="blue"
            size="md"
            onClick={() => navigate(`/markets/${marketId}/practices`)}
            _hover={{ transform: 'translateY(-1px)' }}
            transition="all 0.2s ease-in-out"
          >
            Manage Practices
          </Button>
        </Flex>
      )}
    </Box>
  );
};

export default MarketClinicClosures;
